import { defineStore } from 'pinia';
import { Preferences } from '@capacitor/preferences';
import { watch } from 'vue';
import { useUserDataStore } from '@/store/userDataStore'; // Import the user dat
import { getCookieValue } from '@/services/apiServiceCSRF'; // Import the function to get cookie value

// Interface for the JWT payload. Define new fields here with appropriate types.
interface JwtPayload {
  exp?: number; // Token expiration time, mandatory for expiry checks
  iat?: number; // Issued at time, optional
  nbf?: number; // Not before time, optional
  iss?: string; // Issuer of the token, optional
  userName?: string; // Custom field for user name
  personId?: string; // Custom field for person ID
}


// Interface defining the state structure in the store
interface UserState {
  token: string | undefined; // JWT token or undefined if not fetched yet
  isTokenExpired: boolean; // True if the token has expired, false otherwise
  initialized: boolean; // True if the store has been initialized
}

// Function to setup a watcher to react to token changes and refresh user data
function setupAuthWatcher() {
  const userStore = useUserStore();
  const userDataStore = useUserDataStore();

  let initialLoad = true; // Track initial load

  watch(
    () => userStore.token,
    (newToken, oldToken) => {
      if (initialLoad) {
        initialLoad = false; // Skip fetching on initial load
      } else if (newToken !== oldToken) {
        userDataStore.fetchUserData(); // Trigger only on token change after initial load
      }
    },
    { immediate: true },
  );
}

// Definition of the user store using Pinia
export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    token: undefined,
    isTokenExpired: true,
    initialized: false, // Track if the store has been initialized
  }),
  getters: {
    isLoggedIn: (state): boolean => !state.isTokenExpired,
    isLoggedOut: (state): boolean =>  state.isTokenExpired,
  },
  actions: {
    setToken(token: string = '') {
      // console.groupCollapsed('JWT Token Fetching');
      this.token = token;
      this.initialized = true; // Mark the store as initialized
      if (token) {
        Preferences.set({ key: 'tokenV1', value: token });
        this.decodeToken(token);
        console.log('Token Set:', token);
      } else {
        this.isTokenExpired = true;
        Preferences.remove({ key : 'tokenV1'});
        console.log('User is Logged Out (No token)');
      }
      // console.groupEnd();
    },

    decodeToken(token: string) {
      // console.groupCollapsed('JWT Token Processing');
      try {
        const user = this.extractUserFromJWT(token);
        // console.log('Decoded Token:', user);

        if (user && user.exp && Date.now() >= user.exp * 1000) {
          console.warn('Token has expired');
          this.isTokenExpired = true;
          Preferences.remove({ key : 'tokenV1'});
        } else if (user) {
          this.isTokenExpired = false;
          // console.log('User Info Set:', JSON.stringify(this.userInfo, null, 2));
        } else {
          console.warn('Invalid token');
          this.isTokenExpired = true;
          Preferences.remove({ key : 'tokenV1'});
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        this.isTokenExpired = true;
        Preferences.remove({ key : 'tokenV1'});
      }
      // console.groupEnd();
    },

    extractUserFromJWT(token: string): JwtPayload | null {
      try {
        const payload = token.split('.')[1];
        const decodedPayload = atob(payload);
        return JSON.parse(decodedPayload);
      } catch {
        return null;
      }
    },

    async initializeStore() {
      const token = await Preferences.get({ key: 'tokenV1' });
      if (token.value) {
        // console.groupCollapsed('Initializing Store with token');
        this.setToken(token.value);
        // console.groupEnd();
      } else {
        console.log('No token found in localStorage, trying the cookie');
        //check if the cookie exists
        let cookieToken = getCookieValue('SquashlevelsJWT');
        if (cookieToken) {
          console.log('Initializing Store with cookie token');
          this.setToken(cookieToken);
        }
        else {
          console.log('No token found in localStorage or cookie, user is logged out');
        }
      }

      setupAuthWatcher(); // Setup watcher to react to token changes
    },
  },
});
